import { useState } from 'react'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	RefreshCw,
	TrendingUp,
	AlertTriangle,
	CheckCircle2,
	Coins,
	BarChart3,
} from 'lucide-react'
import { Button } from '@/common/ui/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import { Progress } from '@/common/ui/progress'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import Spinner from '@/common/components/Spinner'
import useQueryGetPaymentGatewayAnalytics from '@/modules/bank/api/hooks/useQueryGetPaymentGatewayAnalytics'
import { formatCurrency } from '@/common/lib/utils'
import { toast } from 'sonner'
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
	LineChart,
	Line,
	PieChart,
	Pie,
	Cell,
} from 'recharts'
import type {
	PaymentGatewayAnalyticsItem,
	PaymentGatewayHourlyBucket,
} from '@/modules/bank/type'
import TransactionStatisticsListing from '@/modules/transaction/components/TransactionStatisticsListing'

function formatSeconds(sec: number): string {
	if (sec <= 0) return '-'
	if (sec < 60) return `${sec}s`
	const m = Math.floor(sec / 60)
	const s = sec % 60
	return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function RateBar({
	successRate,
	failRate,
}: {
	successRate: number
	failRate: number
}) {
	return (
		<div className="space-y-1">
			<div className="flex items-center justify-between text-xs">
				<span className="flex items-center gap-1 text-green-600">
					<CheckCircle2 className="h-3 w-3" />
					สำเร็จ {successRate}%
				</span>
				<span className="flex items-center gap-1 text-red-500">
					<AlertTriangle className="h-3 w-3" />
					ล้มเหลว {failRate}%
				</span>
			</div>
			<div className="flex h-2 overflow-hidden rounded-full bg-muted">
				<div
					className="bg-green-500 transition-all"
					style={{ width: `${successRate}%` }}
				/>
				<div
					className="bg-red-400 transition-all"
					style={{ width: `${failRate}%` }}
				/>
			</div>
		</div>
	)
}

function StatRow({
	label,
	value,
	sub,
}: {
	label: string
	value: string | number
	sub?: string
}) {
	return (
		<div className="flex items-center justify-between py-1.5 text-sm">
			<span className="text-muted-foreground">{label}</span>
			<span className="font-medium">
				{value}
				{sub && (
					<span className="ml-1 text-xs text-muted-foreground">{sub}</span>
				)}
			</span>
		</div>
	)
}

function HourlyChart({
	data,
	color,
}: {
	data: PaymentGatewayHourlyBucket[]
	color: string
}) {
	const chartData = data.map((d) => ({
		hour: `${d.hour}:00`,
		จำนวน: d.count,
		ยอด: d.amount,
	}))
	return (
		<ResponsiveContainer width="100%" height={160}>
			<BarChart
				data={chartData}
				margin={{ top: 2, right: 4, left: -20, bottom: 0 }}
			>
				<CartesianGrid strokeDasharray="3 3" vertical={false} />
				<XAxis
					dataKey="hour"
					tick={{ fontSize: 10 }}
					interval={3}
					tickLine={false}
				/>
				<YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
				<Tooltip
					formatter={(v: number, name: string) =>
						name === 'ยอด' ? [formatCurrency(v), name] : [v, name]
					}
					labelFormatter={(l) => `ช่วงเวลา ${l}`}
				/>
				<Bar dataKey="จำนวน" fill={color} radius={[2, 2, 0, 0]} />
			</BarChart>
		</ResponsiveContainer>
	)
}

function GatewayCard({
	item,
	rank,
}: {
	item: PaymentGatewayAnalyticsItem
	rank: number
}) {
	const [showDeposit, setShowDeposit] = useState(true)
	const active = showDeposit ? item.deposit : item.withdrawal
	const peakDepositHour = item.depositHourlyDistribution.reduce(
		(best, h) => (h.count > best.count ? h : best),
		item.depositHourlyDistribution[0],
	)
	const peakWithdrawHour = item.withdrawalHourlyDistribution.reduce(
		(best, h) => (h.count > best.count ? h : best),
		item.withdrawalHourlyDistribution[0],
	)

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between">
					<div>
						<div className="flex items-center gap-2">
							<span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
								{rank}
							</span>
							<CardTitle className="text-base">{item.displayName}</CardTitle>
						</div>
						<CardDescription className="mt-0.5">
							{item.bankProviderName}
						</CardDescription>
					</div>
					<div className="flex gap-1">
						<Button
							size="sm"
							variant={showDeposit ? 'default' : 'outline'}
							className="h-7 px-2 text-xs"
							onClick={() => setShowDeposit(true)}
						>
							<ArrowDownIcon className="mr-1 h-3 w-3" /> ฝาก
						</Button>
						<Button
							size="sm"
							variant={!showDeposit ? 'default' : 'outline'}
							className="h-7 px-2 text-xs"
							onClick={() => setShowDeposit(false)}
						>
							<ArrowUpIcon className="mr-1 h-3 w-3" /> ถอน
						</Button>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Rate bar */}
				<RateBar successRate={active.successRate} failRate={active.failRate} />

				{/* Key numbers */}
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
					<div className="rounded-lg bg-muted/40 p-2 text-center">
						<p className="text-xs text-muted-foreground">ยอดรวม</p>
						<p className="text-sm font-bold">
							{formatCurrency(active.totalAmount)}
						</p>
					</div>
					<div className="rounded-lg bg-muted/40 p-2 text-center">
						<p className="text-xs text-muted-foreground">สำเร็จ/ทั้งหมด</p>
						<p className="text-sm font-bold">
							{active.totalCount.toLocaleString('th-TH')}/
							{active.totalAllCount.toLocaleString('th-TH')}
						</p>
					</div>
					<div className="rounded-lg bg-muted/40 p-2 text-center">
						<p className="text-xs text-muted-foreground">เฉลี่ยต่อครั้ง</p>
						<p className="text-sm font-bold">
							{formatCurrency(active.avgAmount)}
						</p>
					</div>
					<div className="rounded-lg bg-muted/40 p-2 text-center">
						<p className="text-xs text-muted-foreground">เวลาเฉลี่ย</p>
						<p className="text-sm font-bold">
							{formatSeconds(active.avgProcessingTimeSec)}
						</p>
					</div>
				</div>

				{/* Detail rows */}
				<div className="divide-y">
					<StatRow
						label="ยอดสูงสุด / ต่ำสุด"
						value={`${formatCurrency(active.maxAmount)} / ${formatCurrency(active.minAmount)}`}
					/>
					<StatRow
						label="เวลา (median)"
						value={formatSeconds(active.medianProcessingTimeSec)}
					/>
					<StatRow
						label="ค่าธรรมเนียมรวม"
						value={formatCurrency(active.totalFeeAmount)}
					/>
					<StatRow
						label="รายการล้มเหลว"
						value={active.failedCount.toLocaleString('th-TH')}
						sub="รายการ"
					/>
					<StatRow
						label="ชั่วโมงยอดนิยม"
						value={
							showDeposit
								? `${peakDepositHour?.hour ?? 0}:00 (${peakDepositHour?.count ?? 0} รายการ)`
								: `${peakWithdrawHour?.hour ?? 0}:00 (${peakWithdrawHour?.count ?? 0} รายการ)`
						}
					/>
				</div>

				{/* Hourly chart */}
				<div>
					<p className="mb-1 text-xs font-medium text-muted-foreground">
						การกระจายตามชั่วโมง (จำนวนรายการ)
					</p>
					<HourlyChart
						data={
							showDeposit
								? item.depositHourlyDistribution
								: item.withdrawalHourlyDistribution
						}
						color={showDeposit ? '#22c55e' : '#ef4444'}
					/>
				</div>
			</CardContent>
		</Card>
	)
}

const COLORS = [
	'#22c55e',
	'#3b82f6',
	'#f59e0b',
	'#8b5cf6',
	'#ec4899',
	'#14b8a6',
	'#f97316',
	'#6366f1',
]

export default function PaymentGatewayAnalyticsListing() {
	const { dateFilter } = useDateFilter()

	const { data, isLoading, refetch, error } =
		useQueryGetPaymentGatewayAnalytics({
			startDate: dateFilter.startDate || undefined,
			endDate: dateFilter.endDate || undefined,
		})

	const handleRefresh = async () => {
		try {
			await refetch()
			toast.success('ข้อมูลได้รับการอัปเดตแล้ว')
		} catch {
			toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล')
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (error) {
		return (
			<Card>
				<CardContent className="flex h-64 flex-col items-center justify-center space-y-4">
					<p className="text-muted-foreground">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
					<Button onClick={handleRefresh} variant="outline">
						<RefreshCw className="mr-2 h-4 w-4" />
						ลองใหม่
					</Button>
				</CardContent>
			</Card>
		)
	}

	const items = data?.data ?? []

	// Aggregate totals
	const totalDepositAmount = items.reduce(
		(s, i) => s + i.deposit.totalAmount,
		0,
	)
	const totalDepositCount = items.reduce((s, i) => s + i.deposit.totalCount, 0)
	const totalDepositAll = items.reduce((s, i) => s + i.deposit.totalAllCount, 0)
	const totalWithdrawAmount = items.reduce(
		(s, i) => s + i.withdrawal.totalAmount,
		0,
	)
	const totalWithdrawCount = items.reduce(
		(s, i) => s + i.withdrawal.totalCount,
		0,
	)
	const totalWithdrawAll = items.reduce(
		(s, i) => s + i.withdrawal.totalAllCount,
		0,
	)
	const totalFee = items.reduce(
		(s, i) => s + i.deposit.totalFeeAmount + i.withdrawal.totalFeeAmount,
		0,
	)

	const overallDepositSuccessRate =
		totalDepositAll > 0
			? Math.round((totalDepositCount / totalDepositAll) * 1000) / 10
			: 0
	const overallWithdrawSuccessRate =
		totalWithdrawAll > 0
			? Math.round((totalWithdrawCount / totalWithdrawAll) * 1000) / 10
			: 0

	// Chart: deposit comparison
	const barChartData = items.map((item) => ({
		name:
			item.displayName.length > 10
				? item.displayName.slice(0, 10) + '…'
				: item.displayName,
		ฝาก: item.deposit.totalAmount,
		ถอน: item.withdrawal.totalAmount,
	}))

	// Pie: deposit share
	const pieData = items
		.filter((i) => i.deposit.totalAmount > 0)
		.map((i) => ({ name: i.displayName, value: i.deposit.totalAmount }))

	// Aggregate hourly across all gateways
	const allDepositHourly: PaymentGatewayHourlyBucket[] = Array.from(
		{ length: 24 },
		(_, h) => ({
			hour: h,
			count: items.reduce(
				(s, i) => s + (i.depositHourlyDistribution[h]?.count ?? 0),
				0,
			),
			amount: items.reduce(
				(s, i) => s + (i.depositHourlyDistribution[h]?.amount ?? 0),
				0,
			),
		}),
	)
	const peakHour = allDepositHourly.reduce(
		(best, h) => (h.count > best.count ? h : best),
		allDepositHourly[0],
	)

	// Best/worst by success rate
	const activeItems = items.filter((i) => i.deposit.totalAllCount > 0)
	const bestSuccessRate =
		activeItems.length > 0
			? activeItems.reduce(
					(best, i) =>
						i.deposit.successRate > best.deposit.successRate ? i : best,
					activeItems[0],
				)
			: null
	const worstSuccessRate =
		activeItems.length > 0
			? activeItems.reduce(
					(worst, i) =>
						i.deposit.failRate > worst.deposit.failRate ? i : worst,
					activeItems[0],
				)
			: null

	// Best avg amount (deposit)
	const bestAvgAmount =
		activeItems.length > 0
			? activeItems.reduce(
					(best, i) =>
						i.deposit.avgAmount > best.deposit.avgAmount ? i : best,
					activeItems[0],
				)
			: null

	return (
		<div className="space-y-6 px-4 sm:px-0">
			{/* Header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div className="space-y-1">
					<h1 className="text-2xl font-bold tracking-tight">
						สรุปภาพรวมธนาคาร
					</h1>
					<p className="text-sm text-muted-foreground">
						วิเคราะห์การฝาก-ถอนแยกตามธนาคารออโต้ (method = PAYMENT_GATEWAY)
					</p>
				</div>
				<div className="flex flex-col gap-3 sm:items-end sm:gap-4">
					<DateRangePicker />
					<div className="flex items-center gap-2">
						<DateRangeButtons />
						<Button onClick={handleRefresh} variant="outline" size="sm">
							<RefreshCw className="mr-2 h-4 w-4" />
							รีเฟรช
						</Button>
					</div>
				</div>
			</div>

			<Separator />

			{items.length === 0 ? (
				<Card>
					<CardContent className="flex h-32 items-center justify-center">
						<p className="text-muted-foreground">ไม่พบธนาคารออโต้</p>
					</CardContent>
				</Card>
			) : (
				<>
					{/* Summary KPI cards */}
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">ยอดฝากรวม</CardTitle>
								<ArrowDownIcon className="h-4 w-4 text-green-600" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold text-green-700">
									{formatCurrency(totalDepositAmount)}
								</div>
								<p className="text-xs text-muted-foreground">
									{totalDepositCount.toLocaleString('th-TH')}/
									{totalDepositAll.toLocaleString('th-TH')} รายการ
								</p>
								<div className="mt-2">
									<Progress
										value={overallDepositSuccessRate}
										className="h-1.5"
									/>
									<p className="mt-0.5 text-xs text-muted-foreground">
										Success rate {overallDepositSuccessRate}%
									</p>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">ยอดถอนรวม</CardTitle>
								<ArrowUpIcon className="h-4 w-4 text-red-600" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold text-red-700">
									{formatCurrency(totalWithdrawAmount)}
								</div>
								<p className="text-xs text-muted-foreground">
									{totalWithdrawCount.toLocaleString('th-TH')}/
									{totalWithdrawAll.toLocaleString('th-TH')} รายการ
								</p>
								<div className="mt-2">
									<Progress
										value={overallWithdrawSuccessRate}
										className="h-1.5"
									/>
									<p className="mt-0.5 text-xs text-muted-foreground">
										Success rate {overallWithdrawSuccessRate}%
									</p>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									ชั่วโมง peak (ฝาก)
								</CardTitle>
								<TrendingUp className="h-4 w-4 text-blue-600" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">
									{peakHour.hour}:00 – {peakHour.hour + 1}:00
								</div>
								<p className="text-xs text-muted-foreground">
									{peakHour.count.toLocaleString('th-TH')} รายการ •{' '}
									{formatCurrency(peakHour.amount)}
								</p>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									ค่าธรรมเนียมรวม
								</CardTitle>
								<Coins className="h-4 w-4 text-amber-600" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">
									{formatCurrency(totalFee)}
								</div>
								<p className="text-xs text-muted-foreground">
									ฝาก + ถอน ทุก Gateway
								</p>
							</CardContent>
						</Card>
					</div>

					{/* Insights row */}
					{(bestSuccessRate || worstSuccessRate || bestAvgAmount) && (
						<div className="grid gap-3 sm:grid-cols-3">
							{bestSuccessRate && (
								<Card className="border-green-200 bg-green-50/40 dark:border-green-900 dark:bg-green-950/20">
									<CardContent className="pt-4">
										<div className="flex items-center gap-2 text-green-700 dark:text-green-400">
											<CheckCircle2 className="h-4 w-4" />
											<span className="text-xs font-semibold">
												Success rate สูงสุด (ฝาก)
											</span>
										</div>
										<p className="mt-1 font-bold">
											{bestSuccessRate.displayName}
										</p>
										<p className="text-sm text-green-600 dark:text-green-500">
											{bestSuccessRate.deposit.successRate}% (
											{bestSuccessRate.deposit.totalCount}/
											{bestSuccessRate.deposit.totalAllCount})
										</p>
									</CardContent>
								</Card>
							)}
							{worstSuccessRate && worstSuccessRate.deposit.failRate > 0 && (
								<Card className="border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/20">
									<CardContent className="pt-4">
										<div className="flex items-center gap-2 text-red-700 dark:text-red-400">
											<AlertTriangle className="h-4 w-4" />
											<span className="text-xs font-semibold">
												Fail rate สูงสุด (ฝาก)
											</span>
										</div>
										<p className="mt-1 font-bold">
											{worstSuccessRate.displayName}
										</p>
										<p className="text-sm text-red-600 dark:text-red-500">
											{worstSuccessRate.deposit.failRate}% (
											{worstSuccessRate.deposit.failedCount} รายการ)
										</p>
									</CardContent>
								</Card>
							)}
							{bestAvgAmount && (
								<Card className="border-blue-200 bg-blue-50/40 dark:border-blue-900 dark:bg-blue-950/20">
									<CardContent className="pt-4">
										<div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
											<BarChart3 className="h-4 w-4" />
											<span className="text-xs font-semibold">
												ค่าเฉลี่ยต่อครั้งสูงสุด (ฝาก)
											</span>
										</div>
										<p className="mt-1 font-bold">
											{bestAvgAmount.displayName}
										</p>
										<p className="text-sm text-blue-600 dark:text-blue-500">
											{formatCurrency(bestAvgAmount.deposit.avgAmount)} / รายการ
										</p>
									</CardContent>
								</Card>
							)}
						</div>
					)}

					{/* Charts row */}
					<div className="grid gap-6 lg:grid-cols-2">
						{/* Deposit vs Withdrawal bar */}
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">เปรียบเทียบยอดฝาก-ถอน</CardTitle>
							</CardHeader>
							<CardContent>
								<ResponsiveContainer width="100%" height={260}>
									<BarChart
										data={barChartData}
										margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
									>
										<CartesianGrid strokeDasharray="3 3" vertical={false} />
										<XAxis
											dataKey="name"
											tick={{ fontSize: 11 }}
											tickLine={false}
										/>
										<YAxis
											tickFormatter={(v: number) =>
												v >= 1_000_000
													? `${(v / 1_000_000).toFixed(1)}M`
													: v >= 1_000
														? `${(v / 1_000).toFixed(0)}K`
														: String(v)
											}
											tick={{ fontSize: 10 }}
											tickLine={false}
											axisLine={false}
										/>
										<Tooltip
											formatter={(v: number) => [formatCurrency(v), undefined]}
										/>
										<Legend />
										<Bar dataKey="ฝาก" fill="#22c55e" radius={[4, 4, 0, 0]} />
										<Bar dataKey="ถอน" fill="#ef4444" radius={[4, 4, 0, 0]} />
									</BarChart>
								</ResponsiveContainer>
							</CardContent>
						</Card>

						{/* Deposit share pie */}
						{pieData.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="text-sm">สัดส่วนยอดฝาก</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex items-center gap-4">
										<ResponsiveContainer width="60%" height={220}>
											<PieChart>
												<Pie
													data={pieData}
													cx="50%"
													cy="50%"
													innerRadius={55}
													outerRadius={90}
													paddingAngle={2}
													dataKey="value"
												>
													{pieData.map((_, index) => (
														<Cell
															key={index}
															fill={COLORS[index % COLORS.length]}
														/>
													))}
												</Pie>
												<Tooltip
													formatter={(v: number) => [formatCurrency(v), '']}
												/>
											</PieChart>
										</ResponsiveContainer>
										<div className="flex-1 space-y-1.5">
											{pieData.map((d, i) => (
												<div
													key={i}
													className="flex items-center gap-2 text-xs"
												>
													<span
														className="h-2.5 w-2.5 shrink-0 rounded-full"
														style={{ background: COLORS[i % COLORS.length] }}
													/>
													<span className="truncate text-muted-foreground">
														{d.name}
													</span>
													<span className="ml-auto font-medium">
														{totalDepositAmount > 0
															? Math.round((d.value / totalDepositAmount) * 100)
															: 0}
														%
													</span>
												</div>
											))}
										</div>
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					{/* Global hourly trend */}
					<Card>
						<CardHeader>
							<CardTitle className="text-sm">
								ปริมาณการฝากตามชั่วโมง (ภาพรวมทุก Gateway)
							</CardTitle>
							<CardDescription>
								จำนวนรายการฝากในแต่ละช่วงชั่วโมงของวัน
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ResponsiveContainer width="100%" height={180}>
								<LineChart
									data={allDepositHourly.map((d) => ({
										hour: `${d.hour}:00`,
										จำนวน: d.count,
									}))}
									margin={{ top: 4, right: 8, left: -12, bottom: 0 }}
								>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis
										dataKey="hour"
										tick={{ fontSize: 10 }}
										interval={3}
										tickLine={false}
									/>
									<YAxis
										tick={{ fontSize: 10 }}
										tickLine={false}
										axisLine={false}
									/>
									<Tooltip labelFormatter={(l) => `ช่วง ${l}`} />
									<Line
										type="monotone"
										dataKey="จำนวน"
										stroke="#3b82f6"
										strokeWidth={2}
										dot={false}
									/>
								</LineChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>

					{/* Per-gateway detail cards */}
					<div>
						<h2 className="mb-3 text-lg font-semibold">
							รายละเอียดแต่ละ Gateway
						</h2>
						<div className="grid gap-4 lg:grid-cols-2">
							{items.map((item, i) => (
								<GatewayCard key={item.id} item={item} rank={i + 1} />
							))}
						</div>
					</div>

					{/* Transaction Statistics by Channel */}
					<TransactionStatisticsListing
						startDate={dateFilter.startDate || undefined}
						endDate={dateFilter.endDate || undefined}
					/>
				</>
			)}
		</div>
	)
}
